import {
  type JSX,
  createSignal,
  onMount,
  onCleanup,
  For,
  createMemo,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { IssuePanelProps } from '../../types/issue.types';
import { translate } from '@fulcrum/lib';
import { initWebComponents } from '../../init-web-components';

export function IssuePanel(props: Readonly<IssuePanelProps>): JSX.Element {
  const [selectedSortOption, setSelectedSortOption] = createSignal(
    translate('TC.Common.CreationDate')
  );
  const [filterDropdownOpen, setFilterDropdownOpen] = createSignal(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = createSignal<{
    left: number;
    top: number;
  }>();
  const [selectedFilter, setSelectedFilter] = createSignal(
    translate('TC.Topic.ActiveTopics3D')
  );

  // Filter options
  const filterOptions = [
    translate('TC.Topic.AllProjectTopics'),
    translate('TC.Topic.ActiveTopics3D'),
    translate('TC.Common.AssignedToMe'),
    translate('TC.Topic.Followed'),
    translate('TC.Common.CreatedByMe'),
    translate('TC.Topic.FromThisModel'),
  ];

  const sortDropDownOptions = [
    translate('TC.Common.AssignedTo'),
    translate('TC.Common.Author'),
    translate('TC.Common.CreationDate'),
    translate('TC.Common.DateModified'),
    translate('TC.Common.Priority'),
    translate('TC.Common.Status'),
    translate('TC.Common.Title'),
    translate('TC.Common.Type'),
    translate('tags'),
  ];

  // Handle outside clicks to close dropdown
  let dropdownRef: HTMLDivElement | undefined;
  let filterAnchorRef: HTMLDivElement | undefined;
  let filterOptionsRef: HTMLDivElement | undefined;

  // This panel renders inside the <tc-issue-management> shadow root, so a
  // document-level listener sees `event.target` retargeted to the host element
  // and `dropdownRef.contains(target)` is always false. `composedPath()` keeps
  // the real path across the shadow boundary, and also covers the options pane,
  // which is portalled into <body> and so is not a child of `dropdownRef`.
  const isClickInsideDropdown = (event: MouseEvent): boolean => {
    const path = event.composedPath();
    return (
      (!!dropdownRef && path.includes(dropdownRef)) ||
      (!!filterOptionsRef && path.includes(filterOptionsRef))
    );
  };

  const handleOutsideClick = (event: MouseEvent): void => {
    if (!isClickInsideDropdown(event)) {
      setFilterDropdownOpen(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      setFilterDropdownOpen(false);
    }
  };

  onMount(() => {
    initWebComponents();
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
  });

  onCleanup(() => {
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleKeyDown);
  });

  const toggleFilterDropdown = (event: MouseEvent): void => {
    event.stopPropagation();
    if (filterAnchorRef) {
      const bounds = filterAnchorRef.getBoundingClientRect();
      setFilterDropdownPosition({ left: bounds.left, top: bounds.bottom });
    }
    setFilterDropdownOpen(!filterDropdownOpen());
  };

  const selectFilterOption = (option: string, event: MouseEvent): void => {
    event.stopPropagation();
    setSelectedFilter(option);
    setFilterDropdownOpen(false);
    props.onFilter?.(option);
  };

  // Extract filter dropdown for titleSectionRight
  const filterDropdown = (
    <div
      ref={dropdownRef}
      class="dropdown-pane-container fixed-header-filter-size viewerPanel-dropdown relative z-dropdown"
      data-cy="regions-dropdown"
      data-testid="issue-management-filter-dropdown"
    >
      <div
        ref={filterAnchorRef}
        class="name dropdownpane-link text-ellipsis viewerPanel-link"
        onClick={toggleFilterDropdown}
        style={{ cursor: 'pointer' }}
      >
        <span class="text-ellipsis">{selectedFilter()}</span>
        <i
          class="icon-font tc-icon-arrow-drop-down"
          style={`transition: transform 0.2s ease; transform: ${
            filterDropdownOpen() ? 'rotate(180deg)' : 'rotate(0deg)'
          }`}
        />
      </div>
      <Show when={filterDropdownOpen() && filterDropdownPosition()}>
        {(position) => (
          <Portal>
            <div
              ref={filterOptionsRef}
              class="dropdown-pane is-open z-dropdown-elevated"
              data-testid="issue-management-filter-options"
              style={`position: fixed; top: ${position().top}px; left: ${position().left}px; min-width: 200px;`}
            >
              <ul class="dropdown-list">
                <For each={filterOptions}>
                  {(option: string) => (
                    <li
                      value={option}
                      class={`list-item ${selectedFilter() === option ? 'active' : ''}`}
                      data-cy={option}
                      onClick={(event) => selectFilterOption(option, event)}
                      style={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f5f5f5')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <span class="block text">{option}</span>
                      {selectedFilter() === option && (
                        <i class="icon-font tc-icon-check" />
                      )}
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Portal>
        )}
      </Show>
    </div>
  );

  // Check if user has permission to create issues
  // Supports both new ('issue:create') and legacy ('ISSUE_CREATE') action formats
  const canCreateIssue = (): boolean => {
    const actions = props.configurationData?._actions;
    if (!actions || !Array.isArray(actions)) return false;
    return (
      actions.includes('issue:create') ||
      (actions as string[]).includes('ISSUE_CREATE')
    );
  };

  const getActionButtons = (): JSX.Element[] => [
    <div>
      <tc-custom-tooltip
        text={
          !canCreateIssue()
            ? translate('TC.Common.PermissionDeniedWarning')
            : 'Add new issue'
        }
        position="bottom"
        notDynamic={true}
      >
        {/* @ts-expect-error -- tc-icon-button is a custom web component not recognized by TypeScript */}
        <tc-icon-button
          icon="tc-icon-content-add"
          size="icon-medium"
          color="tertiary"
          class="dropdownpane-link"
          data-cy="add-topic-button"
          disabled={!canCreateIssue()}
          on:click={() => canCreateIssue() && props.onAddIssue?.()}
        />
      </tc-custom-tooltip>
    </div>,

    // Sort dropdown
    <div>
      <div
        style="
    width: 40px;"
      >
        <tc-custom-tooltip
          text="Sort issues"
          position="bottom"
          notDynamic={true}
        >
          {/* Simple dropdown menu web component with portal support */}
          {/* @ts-expect-error -- tc-dropdown-menu is a custom web component not recognized by TypeScript */}
          <tc-dropdown-menu
            type="icon"
            // eslint-disable-next-line @typescript-eslint/naming-convention -- index_0 is a property name from web component API
            add-group-titles={{ index_0: `${translate('TC.Common.SortBy')}` }}
            anchor-icon="icon-font tc-icon-sort"
            anchor-class-name="icon-medium tertiary icon-circle"
            drop-down-options={sortDropDownOptions}
            position="right"
            style="z-index: 1000"
            selected={selectedSortOption()}
            show-on-scroll={true}
            on:selection-changed={(event: CustomEvent) => {
              setSelectedSortOption(event.detail.selected);
              props.onSort?.(event.detail.selected);
            }}
            custom-menu-item-icon={
              props.isSortOrderDesc
                ? 'tc-icon-arrow-line-up'
                : 'tc-icon-arrow-line-down'
            }
          />
        </tc-custom-tooltip>
      </div>
      <div
        class="dropdown-pane w-auto is-open vhidden z-dropdown-elevated"
        id="explorer-dropdown-options-dummy"
      />
    </div>,

    // More menu dropdown
    <div>
      <div
        style="
    width: 40px;"
      >
        <tc-custom-tooltip
          text={translate('TC.Common.MoreOptions')}
          position="bottom"
          notDynamic={true}
        >
          {/* Simple dropdown menu web component with portal support */}
          {/* @ts-expect-error -- tc-dropdown-menu is a custom web component not recognized by TypeScript */}
          <tc-dropdown-menu
            type="icon"
            anchor-icon="icon-font tc-icon-more-vert"
            anchor-class-name="icon-medium tertiary icon-circle"
            drop-down-options={[
              translate('TC.Common.Refresh'),
              translate('TC.Common.Search'),
            ]}
            position="right"
            style="z-index: 1000"
            on:selection-changed={(event: CustomEvent) => {
              const selectedOption = event.detail.selected;
              if (
                selectedOption === translate('TC.Common.Refresh') &&
                props.onRefresh
              ) {
                props.onRefresh();
              } else if (
                selectedOption === translate('TC.Common.Search') &&
                props.onSearch
              ) {
                props.onSearch();
              }
            }}
          />
        </tc-custom-tooltip>
      </div>
      <div class="dropdown-pane w-auto right z-dropdown-elevated">
        <ul class="dropdown-list">
          <li
            value={translate('TC.Common.Refresh')}
            class="list-item"
            data-cy="Refresh"
          >
            {translate('TC.Common.Refresh')}
          </li>
          <li
            value={translate('TC.Common.Search')}
            class="list-item"
            data-cy="Search"
          >
            {translate('TC.Common.Search')}
          </li>
        </ul>
      </div>
    </div>,
  ];

  const rightActions = createMemo(() => {
    if (props.isSearchMode) {
      return [];
    }
    return getActionButtons();
  });

  const panelContentWithToggle = (
    <div class="flex h-full min-h-0 flex-col">
      <Show when={props.showAllMarker !== undefined}>
        <div
          class="head-container mt-0 shrink-0 white-bg"
          style="border-top: 1px solid #e5e7eb;"
        >
          <div class="topics-panel-header-3d !h-auto !py-0">
            <div class="group-items">
              <div class="switch-group switch-left">
                <div class="switch">
                  <input
                    class="switch-input"
                    id="show-topics-3d-toggle"
                    name="show-topics-3d"
                    type="checkbox"
                    checked={props.showAllMarker}
                    onChange={(e) =>
                      props.onChangeMarker?.(e.currentTarget.checked)
                    }
                  />
                  <label class="switch-paddle" for="show-topics-3d-toggle">
                    &nbsp;
                  </label>
                </div>
              </div>
              <div class="block">
                <span>{translate('TC.Topic.ShowTopics3D')}</span>
              </div>
            </div>
          </div>
        </div>
      </Show>
      <div class="min-h-0 flex-1 overflow-hidden">{props.panelContent}</div>
    </div>
  );

  return (
    <div class="trimble-workspace">
      <div class="position-absolute top-0 left-0 right-0 bottom-0">
        <div class="trimble-tabs-content-pane">
          {/* @ts-expect-error -- tc-panel-layout is a custom web component not recognized by TypeScript */}
          <tc-panel-layout
            panel-title={
              props.isSearchMode ? '' : translate('TC.Common.Topics')
            }
            title-data-cy="topics-title"
            title-section-right={
              props.isSearchMode ? props.titleSectionRight : filterDropdown
            }
            class-name={'py-0'}
            header-class-name={
              'px-3 py-1 flex flex-nowrap shrink-0 items-center border-b border-gray-2 relative z-[100]'
            }
            right-actions={rightActions()}
            content={panelContentWithToggle}
          />
        </div>
      </div>
    </div>
  );
}
